/**
 * Imported from DesignManagerIde
 */

'use es6';

import { Component } from 'react';
import PropTypes from 'prop-types';
import emptyFunction from 'react-utils/emptyFunction';
import { jsx as _jsx } from "react/jsx-runtime";
const INPUT_DEBOUNCE_DELAY = 250;

/**
 * Returns an object with the debounced function and properties/methods to manage the debounce.
 *
 * @params {Function} callback
 * @params {Number} delay
 * @return {Object}
 */
function debounceManager(callback, delay) {
  let isDebouncing = false;
  let timeoutId = null;
  let result;
  // Callback wrapper
  function handler(args) {
    timeoutId = null;
    isDebouncing = false;
    result = callback(...args);
  }
  // Debounced function
  function debounced() {
    clearTimeout(timeoutId);
    isDebouncing = true;
    // eslint-disable-next-line prefer-rest-params
    timeoutId = setTimeout(handler, delay, arguments);
    return result;
  }
  return {
    /** Debounced function */
    debounced,
    /** True if debouncing is happening, false otherwise */
    get isDebouncing() {
      return isDebouncing;
    }
  };
}
function isSyntheticEvent(evt) {
  return !!(evt && evt.nativeEvent && evt.persist && evt.preventDefault && evt.stopPropagation);
}

/**
 * Can be used with any input component that implements an interface of
 *  {
 *    value: PropTypes.any,
 *    onChange: PropTypes.func, // onChange(SyntheticEvent) or onChange({value})
 *  }
 * to debounce the `onChange` event and manage the `value` in state.
 */
export default function withDebouncedInput(Input) {
  class WithDebouncedInput extends Component {
    constructor(props) {
      super(props);
      this.onChange = this.onChange.bind(this);
      this.onChangeDebounceMonitor = debounceManager(this.props.onChange, INPUT_DEBOUNCE_DELAY);
      this.state = {
        value: props.value
      };
    }
    componentDidUpdate(prevProps) {
      const {
        value
      } = this.props;
      if (!this.onChangeDebounceMonitor.isDebouncing && prevProps.value !== value) {
        this.setState({
          value
        });
      }
    }
    getValueFromEvent(evt) {
      if (isSyntheticEvent(evt)) {
        // @see {@link https://facebook.github.io/react/docs/events.html#event-pooling}
        evt.persist();
        const {
          currentTarget,
          target
        } = evt;
        return currentTarget ? currentTarget.value : target && target.value;
      }
      if ('value' in evt) {
        return evt.value;
      }
      if (evt.currentTarget) {
        return evt.currentTarget.value;
      }
      if (evt.target) {
        return evt.target.value;
      }
      throw new TypeError(`Invalid onChange event object for debouncing. ${evt}`);
    }
    onChange(evt) {
      const value = this.getValueFromEvent(evt);
      this.setState({
        value
      });
      // eslint-disable-next-line prefer-rest-params
      this.onChangeDebounceMonitor.debounced(...arguments);
    }
    render() {
      return /*#__PURE__*/_jsx(Input, Object.assign({}, this.props, {
        value: this.state.value,
        onChange: this.onChange
      }));
    }
  }
  WithDebouncedInput.defaultProps = {
    onChange: emptyFunction
  };
  WithDebouncedInput.propTypes = {
    value: PropTypes.any,
    onChange: PropTypes.func
  };
  WithDebouncedInput.displayName = `WithDebouncedInput(${Input.displayName || Input.name || ''})`;
  return WithDebouncedInput;
}